/**
 * Shared Form.io schema transformation utilities.
 * Applied consistently in UserFormView, FormioTabRenderer, and FormioForm (FormViewer).
 */

const safeForEachRow = (rows, cb) => {
  if (!rows || !Array.isArray(rows)) return;
  rows.forEach((row) => {
    if (Array.isArray(row)) row.forEach((cell) => cb(cell));
    else if (row && typeof row === 'object') cb(row);
  });
};

/** Deep traversal covering panels, columns, rows, datagrid, editgrid, tabs. */
export const traverseComponents = (components, handler) => {
  if (!components || !Array.isArray(components)) return;
  components.forEach((component) => {
    handler(component);
    if (component.components) traverseComponents(component.components, handler);
    if (component.columns)
      component.columns.forEach((col) => traverseComponents(col.components || [], handler));
    if (component.rows)
      safeForEachRow(component.rows, (cell) => cell?.components && traverseComponents(cell.components, handler));
    if (component.datagrid?.components) traverseComponents(component.datagrid.components, handler);
    if (component.editgrid?.components) traverseComponents(component.editgrid.components, handler);
    if (component.tabs)
      component.tabs.forEach((tab) => traverseComponents(tab.components || [], handler));
    // Tabs stored as component.type === 'tabs' with components as tab array
    if (component.type === 'tabs' && Array.isArray(component.components))
      component.components.forEach((tab) => {
        if (tab?.components) traverseComponents(tab.components, handler);
      });
  });
};

/**
 * Remove trailing asterisks from labels (visual only — does NOT affect validation).
 */
export const stripRequiredUI = (components) => {
  traverseComponents(components, (c) => {
    if (!c) return;
    if (c.label) c.label = c.label.replace(/\s*\*+\s*$/, '').trim();
  });
};

/**
 * Ensure the Form.io submit button is never disabled so admins can always submit.
 */
export const overrideSubmitSettings = (components) => {
  if (!Array.isArray(components)) return;
  traverseComponents(components, (component) => {
    if (component.key === 'submit' && component.type === 'button') {
      component.label = 'Save & Continue';
      component.disableOnInvalid = false;
      component.disabled = false;
      component.hidden = component.hidden || false;
      if (typeof component.hideOnInvalid !== 'undefined') component.hideOnInvalid = false;
      if (typeof component.hideOnSubmit !== 'undefined') component.hideOnSubmit = false;
    }
  });
};

/**
 * Safe calculateValue for phone fields — phoneGroup may use different property names
 * across form versions. Prevents runtime JS errors on forms that reference countryCode.
 */
const SAFE_PHONE_CALCULATE =
  "var pg = data.phoneGroup; if (!pg) return ''; " +
  "var code = (pg.country && typeof pg.country === 'object' && pg.country.dial_code) ? pg.country.dial_code : (pg.countryCode || ''); " +
  "var digits = pg.phoneNumberDigits || pg.phoneNumber1 || pg.phoneNumber || ''; " +
  "return (code && digits) ? code + digits : '';";

export const ensureSafePhoneGroupCalculate = (components) => {
  if (!Array.isArray(components)) return;
  traverseComponents(components, (comp) => {
    const calc = (comp.calculateValue || '').trim();
    if (
      calc &&
      calc.includes('phoneGroup.countryCode') &&
      (comp.key === 'fullPhoneNumber' ||
        comp.key === 'fullPhoneNumber1' ||
        comp.key === 'professionalPhoneNumber' ||
        calc.includes('phoneNumberDigits'))
    ) {
      comp.calculateValue = SAFE_PHONE_CALCULATE;
    }
  });
};

/**
 * Ensure each input component has an id and its label has a matching labelFor
 * so browser accessibility (label→input association) works correctly.
 */
export const ensureMatchingLabelIds = (components) => {
  if (!Array.isArray(components)) return;
  traverseComponents(components, (component) => {
    if (component.key && component.input && !component.id) component.id = component.key;
    if (component.label && component.key && !component.labelFor) component.labelFor = component.key;
  });
};

/**
 * Fix Form.io select defaults that render as the literal string "undefined"
 * in Choices.js (common on Country / Nationality when defaultValue is {}).
 */
export const sanitizeFormioSelectDefaults = (components) => {
  if (!Array.isArray(components)) return;
  traverseComponents(components, (comp) => {
    if (!comp || (comp.type !== 'select' && comp.type !== 'selectboxes')) return;

    if (
      comp.defaultValue !== null &&
      typeof comp.defaultValue === 'object' &&
      !Array.isArray(comp.defaultValue) &&
      Object.keys(comp.defaultValue).length === 0
    ) {
      comp.defaultValue = '';
    }
    if (comp.defaultValue === 'undefined') {
      comp.defaultValue = '';
    }

    let jsonValues = null;
    if (Array.isArray(comp.data?.values)) jsonValues = comp.data.values;
    else if (Array.isArray(comp.data?.json)) jsonValues = comp.data.json;
    else if (typeof comp.data?.json === 'string') {
      try {
        const parsed = JSON.parse(comp.data.json);
        if (Array.isArray(parsed)) jsonValues = parsed;
      } catch (_) {
        /* ignore */
      }
    }

    const sample = Array.isArray(jsonValues) && jsonValues.length ? jsonValues[0] : null;
    if (
      sample &&
      typeof sample === 'object' &&
      Object.prototype.hasOwnProperty.call(sample, 'value') &&
      (!comp.valueProperty || comp.valueProperty === '')
    ) {
      comp.valueProperty = 'value';
    }

    // Dial-code URL lists use { name, dial_code, code } — not { label, value }
    if (
      comp.dataSrc === 'url' &&
      typeof comp.template === 'string' &&
      /\{\{\s*item\.label\s*\}\}/.test(comp.template) &&
      !/\{\{\s*item\.name\s*\}\}/.test(comp.template)
    ) {
      comp.template = '<span>{{ item.name }} ({{ item.dial_code }})</span>';
      if (!comp.valueProperty || comp.valueProperty === '') {
        comp.valueProperty = 'code';
      }
    }
  });
};

/** Clear empty-object / "undefined" select values from submission.data before render. */
export const sanitizeSelectSubmissionValues = (data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (value === 'undefined') {
      data[key] = '';
      return;
    }
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0
    ) {
      data[key] = '';
      return;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      sanitizeSelectSubmissionValues(value);
    }
  });
  return data;
};

/**
 * Form.io hides per-cell labels inside datagrids unless dataGridLabel is true
 * (labels live in thead instead). Prototype layouts hide thead, so enable
 * cell labels (Accommodation, Guest, etc.) so fields render in each row.
 *
 * Respect schema hideLabel — nested Country Code / Phone Number stay hidden.
 * Skip Travel Arrival/Departure containers (CSS ::after paints those titles).
 * Allow other containers (e.g. Guest Phone Number parent label).
 */
export const ensureDatagridCellLabels = (components) => {
  const skipLabelTypes = new Set([
    'fieldset',
    'panel',
    'well',
    'columns',
    'table',
    'datagrid',
    'editgrid',
    'tabs',
  ]);
  const isTravelCardContainer = (comp) => {
    const key = String(comp.key || '').toLowerCase();
    return (
      key === 'arrivalfrom' ||
      key === 'departurefromlosangeles' ||
      key.includes('arrivalfrom') ||
      key.includes('departurefrom')
    );
  };
  const walk = (list, inDatagrid) => {
    if (!Array.isArray(list)) return;
    list.forEach((comp) => {
      if (!comp) return;
      const type = (comp.type || '').toLowerCase();
      if (
        inDatagrid &&
        !skipLabelTypes.has(type) &&
        !(type === 'container' && isTravelCardContainer(comp))
      ) {
        const labelText = (comp.label || comp.title || '').trim();
        if (labelText && comp.hideLabel !== true) {
          // Prototype hides thead — need visible cell label (e.g. Check-in / Guest phone).
          comp.label = labelText;
          comp.dataGridLabel = true;
          // Form.io still may paint empty <label> if hideLabel left undefined/odd.
          comp.hideLabel = false;
        }
      }
      const childInGrid = inDatagrid || type === 'datagrid' || type === 'editgrid';
      if (comp.components) walk(comp.components, childInGrid);
      if (comp.columns) {
        comp.columns.forEach((col) => walk(col.components || [], childInGrid));
      }
      if (comp.rows) {
        safeForEachRow(comp.rows, (cell) => {
          if (cell?.components) walk(cell.components, childInGrid);
        });
      }
      if (comp.tabs) {
        comp.tabs.forEach((tab) => walk(tab.components || [], childInGrid));
      }
    });
  };
  walk(components, false);
};

/**
 * Admin FormViewer / empty preview: ensure datagrids show one blank row.
 * Schemas with initEmpty/noFirstRow only render "+ Add" until a row exists.
 */
export const ensureDatagridPreviewRows = (components) => {
  if (!Array.isArray(components)) return;
  traverseComponents(components, (comp) => {
    const type = (comp.type || '').toLowerCase();
    if (type !== 'datagrid' && type !== 'editgrid') return;
    comp.initEmpty = false;
    comp.noFirstRow = false;
    if (!Array.isArray(comp.defaultValue) || comp.defaultValue.length === 0) {
      comp.defaultValue = [{}];
    }
  });
};

/**
 * Build submission.data with one empty row per datagrid/editgrid key
 * (and nested under container keys when the grid is inside a container).
 */
export const buildDatagridPreviewSubmissionData = (components) => {
  const data = {};
  const walk = (list, target) => {
    if (!Array.isArray(list)) return;
    list.forEach((comp) => {
      if (!comp) return;
      const type = (comp.type || '').toLowerCase();
      if ((type === 'datagrid' || type === 'editgrid') && comp.key) {
        target[comp.key] = [{}];
        return;
      }
      if ((type === 'container' || type === 'address' || type === 'phonegroup') && comp.key) {
        if (!target[comp.key] || typeof target[comp.key] !== 'object') {
          target[comp.key] = {};
        }
        if (comp.components) walk(comp.components, target[comp.key]);
        return;
      }
      if (comp.components) walk(comp.components, target);
      if (comp.columns) {
        comp.columns.forEach((col) => walk(col.components || [], target));
      }
      if (comp.rows) {
        safeForEachRow(comp.rows, (cell) => {
          if (cell?.components) walk(cell.components, target);
        });
      }
      if (comp.tabs) {
        comp.tabs.forEach((tab) => walk(tab.components || [], target));
      }
    });
  };
  walk(components, data);
  return data;
};

/**
 * Date of Birth must accept a fully typed day, month and year (IGFEMS-614).
 * The saved format "dd-MMM-YYYY" breaks Form.io's typing mask: it only turns
 * lowercase y/d/m into digit slots, so "YYYY" stays literal text and the year
 * can't be typed. Lowercase "yyyy" is converted correctly everywhere.
 */
const lowercaseYearTokens = (format) =>
  typeof format === 'string' ? format.replace(/Y/g, 'y') : format;

export const ensureBirthDateTypable = (components) => {
  if (!Array.isArray(components)) return;
  traverseComponents(components, (comp) => {
    if ((comp.type || '').toLowerCase() !== 'datetime') return;
    if (!/birth|^dob$/i.test(`${comp.key || ''} ${comp.label || ''}`)) return;
    comp.format = lowercaseYearTokens(comp.format);
    if (comp.widget && typeof comp.widget === 'object') {
      comp.widget.format = lowercaseYearTokens(comp.widget.format);
    }
  });
};

/**
 * Short fixed lists are plain dropdowns, not search boxes (IGFEMS-652). With
 * search off, Choices.js renders no text input, so nothing can be typed.
 */
const PLAIN_SELECT_KEYS = new Set(['gender', 'minorStatus']);

export const ensurePlainSelects = (components) => {
  if (!Array.isArray(components)) return;
  traverseComponents(components, (comp) => {
    if (comp?.type === 'select' && PLAIN_SELECT_KEYS.has(comp.key)) {
      comp.searchEnabled = false;
    }
  });
};

/**
 * Apply all standard schema transforms in the correct order.
 * Call this once on a cloned schema before passing to <Form>.
 */
export const applyStandardSchemaTransforms = (components) => {
  if (!Array.isArray(components)) return;
  ensureMatchingLabelIds(components);
  stripRequiredUI(components);
  overrideSubmitSettings(components);
  ensureSafePhoneGroupCalculate(components);
  sanitizeFormioSelectDefaults(components);
  ensureBirthDateTypable(components);
  ensurePlainSelects(components);
};

const NON_INPUT_TYPES = new Set([
  'button',
  'htmlelement',
  'content',
  'well',
  'columns',
  'fieldset',
  'panel',
  'tabs',
  'table',
]);

/**
 * Collect top-level keys that this Form.io schema reads/writes in submission.data.
 * Datagrid/editgrid keys are included as a single key (row columns live inside rows).
 */
export function collectFormDataKeys(components) {
  const keys = new Set();
  if (!Array.isArray(components)) return keys;

  traverseComponents(components, (comp) => {
    if (!comp?.key) return;
    const type = (comp.type || '').toLowerCase();
    if (NON_INPUT_TYPES.has(type)) return;

    if (type === 'datagrid' || type === 'editgrid') {
      keys.add(comp.key);
      return;
    }

    if (comp.input === true || type === 'file' || type === 'hidden') {
      keys.add(comp.key);
      return;
    }

    if (type === 'container' || type === 'phonegroup' || type === 'address') {
      keys.add(comp.key);
    }
  });

  return keys;
}

/** Always persist when present (may be hidden / synced outside visible fields). */
export const PARTICIPANT_META_KEYS = new Set([
  'Group_id',
  'groupName',
  'personId',
  'presentCaddies',
  'additionalCaddies',
]);

/** Keys injected at runtime (not Form.io builder fields) — never persist these. */
export const INJECTED_PERSON_DATA_KEYS = new Set([
  'username',
  'userName',
  'participantId',
  'caddies',
  'caddie',
]);

const PERSON_IDENTITY_FROM_RECORD = {
  givenFirstName: (person) => person?.name,
  familyLastName: (person) => person?.lname,
  emailAddress: (person) => person?.email,
  personId: (person) => (person?._id ? String(person._id) : ''),
};

/** Names are edited per participant, never in the CRM (IGFEMS-656): Person only fills blanks. */
const PERSON_FILLS_BLANK_ONLY = new Set(['givenFirstName', 'familyLastName']);

const hasText = (value) => typeof value === 'string' && value.trim() !== '';

const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
/** Participants can edit their saved name, so escape it before putting it in an HTML email. */
export const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch]);

/** The name to show for a participant: their saved name, else the CRM one. */
export function participantDisplayName(personData, person) {
  const firstName = hasText(personData?.givenFirstName) ? personData.givenFirstName.trim() : person?.name || '';
  const lastName = hasText(personData?.familyLastName) ? personData.familyLastName.trim() : person?.lname || '';
  return { firstName, lastName, fullName: `${firstName} ${lastName}`.trim() };
}

/** Saved as an option object or a plain code; an empty value of either shape reads as ''. */
const nationalityCode = (value) =>
  String((value && typeof value === 'object' ? value.value : value) ?? '').trim();

/** Registration's nationality and Accreditation's nationality1 are one answer: a change to either updates the other. */
export function syncNationality(target, previous) {
  if (!target) return target;
  // A blank tab loads showing the other tab's answer, so that is what it is compared with.
  const savedReg = nationalityCode(previous?.nationality);
  const savedAcc = nationalityCode(previous?.nationality1);
  if (nationalityCode(target.nationality) !== (savedReg || savedAcc)) {
    target.nationality1 = target.nationality;
  } else if (nationalityCode(target.nationality1) !== (savedAcc || savedReg)) {
    target.nationality = target.nationality1;
  }
  return target;
}

/**
 * Build Form.io initial data from saved person_data and participant group.
 * When schema is available, only syncs person-record identity fields that exist on the form.
 */
export function buildInitialFormData({
  savedPersonData,
  schemaComponents = null,
  groupId = '',
  groupName = '',
  roleName = '',
  invitationTypeName = '',
  accreditationName = '',
  personRecord = null,
} = {}) {
  const saved =
    savedPersonData && typeof savedPersonData === 'object' && !Array.isArray(savedPersonData)
      ? savedPersonData
      : {};

  const data = {
    ...saved,
    Group_id: groupId || saved.Group_id || '',
    groupName: groupName || saved.groupName || '',
    roleName: roleName || '',
    invitationTypeName: invitationTypeName || '',
    accreditationName: accreditationName || '',
  };

  // A participant answered on one tab only, so the other tab shows that answer.
  if (!nationalityCode(data.nationality) && nationalityCode(data.nationality1)) {
    data.nationality = data.nationality1;
  } else if (!nationalityCode(data.nationality1) && nationalityCode(data.nationality)) {
    data.nationality1 = data.nationality;
  }

  if (!personRecord) return data;

  if (schemaComponents) {
    const formKeys = collectFormDataKeys(schemaComponents);
    Object.entries(PERSON_IDENTITY_FROM_RECORD).forEach(([key, getValue]) => {
      if (!formKeys.has(key)) return;
      if (PERSON_FILLS_BLANK_ONLY.has(key) && hasText(data[key])) return;
      const value = getValue(personRecord);
      if (value != null && value !== '') data[key] = value;
    });
  } else if (personRecord._id) {
    data.personId = String(personRecord._id);
  }

  return data;
}

/** Scoreboard Name has no value saved yet — derive one from first + last name, editable afterward. */
export function applyScoreboardNameFallback(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  if (!data.fullNameForScoreBoard) {
    const combined = [data.givenFirstName, data.familyLastName].filter(Boolean).join(' ').trim();
    if (combined) data.fullNameForScoreBoard = combined;
  }
  return data;
}

/** After a rename, a Scoreboard Name still derived from the old name follows it; a hand-edited one stays. */
export function followScoreboardName(target, previous, person) {
  if (!target) return target;
  const oldName = participantDisplayName(previous, person).fullName;
  const newName = participantDisplayName(target, person).fullName;
  if (newName && newName !== oldName && String(target.fullNameForScoreBoard ?? '').trim() === oldName) {
    target.fullNameForScoreBoard = newName;
  }
  return target;
}

/** Person record wins for email and id; names are per participant, so it only fills a blank name. */
export function preserveParticipantIdentityFromPerson(target, personRecord) {
  if (!target || typeof target !== 'object' || Array.isArray(target)) return target;
  if (!personRecord || !personRecord._id) return target;
  if (!hasText(target.givenFirstName) && personRecord.name) target.givenFirstName = personRecord.name;
  if (!hasText(target.familyLastName) && personRecord.lname) target.familyLastName = personRecord.lname;
  if (personRecord.email) target.emailAddress = personRecord.email;
  target.personId = String(personRecord._id);
  return target;
}

/**
 * Keep only fields defined on the active form, plus values from other tabs (baseline keys
 * not on this schema). Drops injected/UI-only keys (username, flat caddie fields, etc.).
 */
export function filterPersonDataForFormSave(personData, schemaComponents, baseline = {}) {
  if (!personData || typeof personData !== 'object' || Array.isArray(personData)) {
    return personData;
  }

  const formKeys = collectFormDataKeys(schemaComponents);
  if (!formKeys.size) return personData;

  const formKeySet = new Set(formKeys);
  const baselineObj =
    baseline && typeof baseline === 'object' && !Array.isArray(baseline) ? baseline : {};
  const merged = personData;
  const result = {};

  Object.keys(baselineObj).forEach((key) => {
    if (INJECTED_PERSON_DATA_KEYS.has(key)) return;
    if (!formKeySet.has(key) && Object.prototype.hasOwnProperty.call(baselineObj, key)) {
      result[key] = merged[key] !== undefined ? merged[key] : baselineObj[key];
    }
  });

  formKeys.forEach((key) => {
    if (INJECTED_PERSON_DATA_KEYS.has(key)) return;
    if (Object.prototype.hasOwnProperty.call(merged, key)) {
      result[key] = merged[key];
    }
  });

  PARTICIPANT_META_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(merged, key) && merged[key] !== undefined) {
      result[key] = merged[key];
    }
  });

  return result;
}
